import { useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail, homePath } from "../lib/api";
import { toast } from "sonner";
import { ChartLineUp, ShieldCheck, ArrowRight, DeviceMobile, Password, Buildings, UserCircle, Headset } from "@phosphor-icons/react";

const PORTALS = {
  client: { label: "Client", icon: Buildings, email: "client@demo.com", password: "Client@123", tag: "Business owner portal" },
  agent: { label: "Agent", icon: Headset, email: "agent@demo.com", password: "Agent@123", tag: "Virtualeena professional" },
  admin: { label: "Admin", icon: ShieldCheck, email: "aca.anshul28@gmail.com", password: "Admin@123", tag: "Manage clients & agents" },
};
const BIZ_TYPES = ["Private Limited", "LLP", "Partnership", "Proprietorship", "Public Limited", "Others"];

export default function Login() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const preplan = params.get("plan");
  const [portal, setPortal] = useState("client");
  const [authMode, setAuthMode] = useState(preplan ? "signup" : "signin"); // signin | signup
  const [mode, setMode] = useState("password"); // password | otp
  const [email, setEmail] = useState(PORTALS.client.email);
  const [password, setPassword] = useState(PORTALS.client.password);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [twofaUser, setTwofaUser] = useState(null);
  const [code, setCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [su, setSu] = useState({ name: "", email: "", password: "", business_name: "", business_type: "Private Limited", gstin: "", phone: "", city: "", annual_turnover: "< 1 Cr", plan: preplan || "" });

  if (user) return <Navigate to={homePath(user.role)} replace />;

  const pickPortal = (p) => {
    setPortal(p); setAuthMode("signin"); setErr(""); setMode("password"); setOtpSent(false);
    setEmail(PORTALS[p].email); setPassword(PORTALS[p].password);
  };
  const finish = (data) => { setUser(data); nav(homePath(data.role)); };
  const fail = (e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);

  const submitPassword = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_2fa) { setTwofaUser(data.user_id); setCode(""); }
      else finish(data);
    } catch (e) { fail(e); } finally { setLoading(false); }
  };
  const sendForgot = async () => {
    if (!email) { setErr("Enter your email above first"); return; }
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If that email exists, a reset link is on its way ✉");
    } catch { toast.error("Could not send reset link"); }
  };
  const submit2fa = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { finish((await api.post("/auth/2fa/verify-login", { user_id: twofaUser, code })).data); }
    catch (e) { fail(e); } finally { setLoading(false); }
  };
  const requestOtp = async () => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email });
      setOtpSent(true); setCode(data.demo_code || "");
      toast.success(data.demo_code ? `OTP (demo): ${data.demo_code}` : "OTP sent to your email ✉");
    } catch (e) { fail(e); } finally { setLoading(false); }
  };
  const verifyOtp = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { finish((await api.post("/auth/verify-otp", { email, code })).data); }
    catch (e) { fail(e); } finally { setLoading(false); }
  };
  const submitSignup = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { finish((await api.post("/auth/register", su)).data); }
    catch (e) { fail(e); } finally { setLoading(false); }
  };

  const inputCls = "w-full border border-black/20 rounded-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#111111] text-white">
        <div className="flex items-center gap-2">
          <ChartLineUp size={28} weight="fill" color="#fff" />
          <span className="font-head font-black text-xl tracking-tight">VIRTUALEENA</span>
        </div>
        <div>
          <p className="label text-white/50 mb-4">Virtual CFO Platform</p>
          <h1 className="font-head font-black text-5xl leading-none tracking-tighter mb-6">
            Three portals,<br />one command&nbsp;center.
          </h1>
          <p className="text-white/60 max-w-md">
            Clients track work & compliance, Agents deliver the work, Admins run the show — each with a dedicated, secure login.
          </p>
        </div>
        <div className="flex items-center gap-2 text-white/40 text-sm">
          <ShieldCheck size={18} /> Role-based access · OTP · Two-Factor Auth
        </div>
      </div>

      <div className="flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-sm fade-up py-6">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <ChartLineUp size={26} weight="fill" color="#002FA7" />
            <span className="font-head font-black text-lg">VIRTUALEENA</span>
          </div>

          {/* Portal selector */}
          <div className="grid grid-cols-3 gap-1 mb-6 border border-black/10 rounded-sm p-1">
            {Object.entries(PORTALS).map(([k, p]) => (
              <button key={k} data-testid={`portal-${k}`} onClick={() => pickPortal(k)}
                className={`flex flex-col items-center gap-1 py-2.5 rounded-sm text-xs transition-colors ${portal === k ? "bg-[#002FA7] text-white" : "text-[#555] hover:bg-black/5"}`}>
                <p.icon size={18} weight={portal === k ? "fill" : "regular"} /> {p.label}
              </button>
            ))}
          </div>
          <p className="label mb-1">{PORTALS[portal].tag}</p>
          <h2 className="font-head font-black text-3xl tracking-tight mb-6">
            {authMode === "signup" ? "Create account" : twofaUser ? "Verify identity" : "Sign in"}
          </h2>

          {twofaUser ? (
            <form onSubmit={submit2fa} className="space-y-4">
              <p className="text-sm text-[#555] flex items-center gap-2"><ShieldCheck size={18} color="#002FA7" /> Enter the 6-digit code from your authenticator app.</p>
              <input data-testid="twofa-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" inputMode="numeric"
                className={`${inputCls} tracking-[0.4em] text-center`} />
              {err && <p data-testid="login-error" className="text-[#FF3B30] text-sm">{err}</p>}
              <button data-testid="twofa-verify" disabled={loading} className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white py-2.5 rounded-sm font-semibold transition-colors disabled:opacity-60">Verify & Sign In</button>
              <button type="button" onClick={() => setTwofaUser(null)} className="w-full text-sm text-[#888] hover:text-black">← Back</button>
            </form>
          ) : authMode === "signup" ? (
            <form onSubmit={submitSignup} className="space-y-3">
              <p className="text-sm text-[#555] -mt-2 mb-1">Tell us about your business — your portal is set up accordingly.</p>
              {su.plan && (
                <div data-testid="signup-plan-chip" className="flex items-center gap-2 text-sm bg-[#002FA7]/10 text-[#002FA7] px-3 py-2 rounded-sm">
                  <ShieldCheck size={16} weight="fill" /> Signing up for the <b>{su.plan}</b> plan
                </div>
              )}
              <input data-testid="su-business" required placeholder="Business / Company name" value={su.business_name}
                onChange={(e) => setSu({ ...su, business_name: e.target.value })} className={inputCls} />
              <div className="grid grid-cols-2 gap-2">
                <select data-testid="su-type" value={su.business_type} onChange={(e) => setSu({ ...su, business_type: e.target.value })} className={inputCls}>
                  {BIZ_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
                <select data-testid="su-turnover" value={su.annual_turnover} onChange={(e) => setSu({ ...su, annual_turnover: e.target.value })} className={inputCls}>
                  {["< 1 Cr", "1-5 Cr", "5-25 Cr", "25-100 Cr", "100 Cr+"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="su-gstin" placeholder="GSTIN (optional)" value={su.gstin} onChange={(e) => setSu({ ...su, gstin: e.target.value })} className={inputCls} />
                <input data-testid="su-city" placeholder="City" value={su.city} onChange={(e) => setSu({ ...su, city: e.target.value })} className={inputCls} />
              </div>
              <input data-testid="su-name" required placeholder="Your name" value={su.name} onChange={(e) => setSu({ ...su, name: e.target.value })} className={inputCls} />
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="su-email" required type="email" placeholder="Work email" value={su.email} onChange={(e) => setSu({ ...su, email: e.target.value })} className={inputCls} />
                <input data-testid="su-phone" placeholder="Phone" value={su.phone} onChange={(e) => setSu({ ...su, phone: e.target.value })} className={inputCls} />
              </div>
              <input data-testid="su-password" required type="password" placeholder="Create password" value={su.password} onChange={(e) => setSu({ ...su, password: e.target.value })} className={inputCls} />
              {err && <p data-testid="login-error" className="text-[#FF3B30] text-sm">{err}</p>}
              <button data-testid="signup-submit" disabled={loading} className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white py-2.5 rounded-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                {loading ? "Creating…" : "Create Business Account"} <ArrowRight size={18} />
              </button>
              <button type="button" data-testid="to-signin" onClick={() => { setAuthMode("signin"); setErr(""); }} className="w-full text-sm text-[#888] hover:text-black">Already registered? Sign in</button>
            </form>
          ) : (
            <>
              <div className="flex gap-1 mb-5 border border-black/10 rounded-sm p-1">
                <button data-testid="password-tab" onClick={() => { setMode("password"); setErr(""); }}
                  className={`flex-1 text-sm py-2 rounded-sm flex items-center justify-center gap-2 transition-colors ${mode === "password" ? "bg-[#111] text-white" : "text-[#555]"}`}>
                  <Password size={16} /> Password
                </button>
                <button data-testid="otp-tab" onClick={() => { setMode("otp"); setErr(""); setOtpSent(false); }}
                  className={`flex-1 text-sm py-2 rounded-sm flex items-center justify-center gap-2 transition-colors ${mode === "otp" ? "bg-[#111] text-white" : "text-[#555]"}`}>
                  <DeviceMobile size={16} /> OTP
                </button>
              </div>

              {mode === "password" ? (
                <form onSubmit={submitPassword} className="space-y-4">
                  <div><label className="label block mb-1">Email</label>
                    <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} required /></div>
                  <div><label className="label block mb-1">Password</label>
                    <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} required /></div>
                  {err && <p data-testid="login-error" className="text-[#FF3B30] text-sm">{err}</p>}
                  <button data-testid="login-submit" disabled={loading}
                    className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white py-2.5 rounded-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                    {loading ? "Signing in…" : "Sign In"} <ArrowRight size={18} />
                  </button>
                  <button type="button" data-testid="forgot-password" onClick={sendForgot}
                    className="w-full text-xs text-[#888] hover:text-[#002FA7] transition-colors">Forgot password?</button>
                </form>
              ) : (
                <form onSubmit={verifyOtp} className="space-y-4">
                  <div><label className="label block mb-1">Email</label>
                    <input data-testid="otp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} required /></div>
                  {!otpSent ? (
                    <button data-testid="otp-request" type="button" onClick={requestOtp} disabled={loading}
                      className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white py-2.5 rounded-sm font-semibold transition-colors disabled:opacity-60">
                      {loading ? "Sending…" : "Send OTP"}
                    </button>
                  ) : (
                    <>
                      <div><label className="label block mb-1">Enter OTP</label>
                        <input data-testid="otp-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" inputMode="numeric" className={`${inputCls} tracking-[0.4em] text-center`} /></div>
                      {err && <p data-testid="login-error" className="text-[#FF3B30] text-sm">{err}</p>}
                      <button data-testid="otp-verify" disabled={loading} className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white py-2.5 rounded-sm font-semibold transition-colors disabled:opacity-60">Verify & Sign In</button>
                    </>
                  )}
                </form>
              )}

              {portal === "client" && (
                <button data-testid="to-signup" onClick={() => { setAuthMode("signup"); setErr(""); }}
                  className="mt-5 w-full text-sm border border-black/15 rounded-sm py-2.5 hover:bg-black/5 transition-colors flex items-center justify-center gap-2">
                  <UserCircle size={18} /> New business? Create your CFO account
                </button>
              )}
              <p className="mt-4 text-xs text-[#888]">Demo {PORTALS[portal].label}: {PORTALS[portal].email}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
